import { observer } from 'mobx-react-lite'
import { Box, Icon, IconButton, Tooltip, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import type {
  IInboxSlackMapping,
  IntegrationSlackSettingsStore,
} from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSlackSettings/store/IntegrationSlackSettingsStore'
import styles from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSlackSettings/styles.module.scss'
import { SelectInboxes } from '../SelectInboxes/SelectInboxes'

type IMappingRowProps = {
  mapping: IInboxSlackMapping
  store: IntegrationSlackSettingsStore
}

export const MappingRow = observer(({ mapping, store }: IMappingRowProps) => {
  const {
    slackChannels,
    updateMapping,
    removeMapping,
    shouldShowDeleteButton,
    isMappingFullfilled,
    getSlackChannelSelectItems,
    refreshMappings,
  } = store
  const { slackChannelId } = mapping

  const handleInboxesChange = (inboxIds: number[]) => {
    mapping.originalInboxes = [...mapping.inboxes]
    store.updateLocalMapping(mapping, { inboxes: inboxIds })
  }

  const handleSlackChannelChange = (channelId: string) => {
    updateMapping(mapping, { slackChannelId: channelId })
  }

  const slackChannelItems = getSlackChannelSelectItems(mapping)
  const selectedChannelName = slackChannelId
    ? slackChannels.find((channel) => channel.id === mapping.slackChannelId)?.name
    : undefined

  return (
    <Box display='flex' alignItems='center' gap={1} marginTop={2}>
      <SelectInboxes onSelectInboxes={handleInboxesChange} store={store} currentMapping={mapping} />
      <div className={styles.typeIcon}>
        <Icon fontSize={16} icon={'arrowRight'} tertiary />
      </div>
      <SelectField
        setValue={handleSlackChannelChange}
        value={slackChannelId}
        items={slackChannelItems}
        btnProps={{
          text: selectedChannelName ? (
            <Box display='flex' alignItems='center' gap={1}>
              <Icon icon='hash2' tertiary />
              <Typography variant='body-md-regular' ariaLabel='slackChannelName'>
                {selectedChannelName}
              </Typography>
            </Box>
          ) : (
            'Select a Slack channel...'
          ),
          textIconColor: 'var(--content-primary-tertiary)',
          style: {
            minHeight: 40,
            color: !selectedChannelName ? 'var(--content-neutral-primary)' : undefined,
            paddingRight: 8,
          },
        }}
        dropdownProps={{
          propsDropdownList: {
            placeholder: 'Select a Slack channel...',
          },
        }}
        className={styles.selectChannel}
      />
      <div className={styles.refreshButton}>
        {isMappingFullfilled(mapping) && (
          <Tooltip label='Refresh' placement='top'>
            <IconButton
              icon='refresh'
              size='medium'
              variant='icon'
              color='tertiary'
              onClick={refreshMappings}
              ariaLabel='Refresh'
            />
          </Tooltip>
        )}
      </div>
      {shouldShowDeleteButton(mapping) && (
        <Tooltip label='Delete mapping' placement='top'>
          <IconButton
            icon='delete'
            size='medium'
            variant='icon'
            color='tertiary'
            onClick={() => removeMapping(mapping.id)}
            ariaLabel='Delete mapping'
          />
        </Tooltip>
      )}
    </Box>
  )
})
